'use client';

import { c as _c } from "react/compiler-runtime";
import { type UseMutateFunction } from '@tanstack/react-query';
import { type Dispatch, type SetStateAction } from 'react';
import { type PatchAssessmentQuestions } from '@module/mdrt-org/shared/utils/data/patch-assessment-questions';
import { type AssessmentQuestionsTopic } from '@module/mdrt-org/shared/utils/enums/assessment-questions-topic';
import { type MutationError } from '@module/mdrt-org/shared/utils/types/mutation-error';
import { Slider } from '@shared/ui-components/slider/slider';
import { GTM_EVENT_NAME_ASSESSMENT_SCORE_CHANGE } from '@shared/utils/constants/gtm';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { type ServerActionResponse } from '@shared/utils/types/server-action';
import { calculateRoundedAverage } from './helpers';
import styles from './self-assessment-form.module.scss';
type SelfAssessmentFormData = {
  assessmentTopics: Array<{
    questions: Array<{
      question: string;
      questionId: string;
      score: number;
    }>;
    topic: AssessmentQuestionsTopic;
    topicScore: number;
  }>;
};
type SelfAssessmentFormProps = {
  mutateAssessmentQuestions: UseMutateFunction<MutationError | null | Response | ServerActionResponse, Error, PatchAssessmentQuestions, unknown>;
  selfAssessmentFormData: SelfAssessmentFormData;
  setSelfAssessmentFormData: Dispatch<SetStateAction<SelfAssessmentFormData>>;
  topic: AssessmentQuestionsTopic;
};
export const SelfAssessmentForm = t0 => {
  const $ = _c(17);
  const {
    mutateAssessmentQuestions,
    selfAssessmentFormData,
    setSelfAssessmentFormData,
    topic
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== mutateAssessmentQuestions || $[1] !== selfAssessmentFormData || $[2] !== setSelfAssessmentFormData || $[3] !== topic) {
    const currentTopicData = selfAssessmentFormData?.assessmentTopics?.find(topicItem => topicItem.topic === topic);
    const handleScoreChange = async (score, questionId) => {
      if (currentTopicData === undefined) {
        return;
      }
      const updatedCurrentTopicQuestions = currentTopicData?.questions.map(topicItem_0 => {
        if (topicItem_0.questionId !== questionId) {
          return topicItem_0;
        }
        return {
          ...topicItem_0,
          score
        };
      });
      const questionToUpdate = currentTopicData.questions.find(question => question.questionId === questionId.toString());
      if (!questionToUpdate || questionToUpdate.score === score) {
        return;
      }
      const scoresArray = updatedCurrentTopicQuestions?.map(_temp);
      const averageScore = scoresArray ? calculateRoundedAverage(scoresArray) : 0;
      const updatedState = {
        ...selfAssessmentFormData,
        assessmentTopics: selfAssessmentFormData.assessmentTopics.map(item_0 => {
          if (item_0.topic !== topic) {
            return item_0;
          }
          return {
            ...item_0,
            questions: updatedCurrentTopicQuestions,
            topicScore: averageScore
          };
        })
      };
      setSelfAssessmentFormData(updatedState);
      const payload = {
        questions: updatedState?.assessmentTopics.find(item_1 => item_1.topic === topic)?.questions.map(_temp2) ?? [],
        topic
      };
      mutateAssessmentQuestions(payload);
    };
    t4 = styles["assessment-form"];
    t5 = "quiz-form";
    t1 = styles["assessment-form__list"];
    t2 = "quiz-questions-list";
    t3 = currentTopicData?.questions?.map(question_0 => <li className={styles["assessment-form__list-item"]} data-testid="quiz-questions-list-item" key={question_0.questionId}><span aria-hidden="true" id={`quiz-question-${question_0.questionId.toString()}`}>{question_0.question}</span><Slider ariaLabelledBy={`quiz-question-${question_0.questionId.toString()}`} defaultValue={question_0.score} name={`question-${question_0.questionId}`} onChangeHandler={score_0 => {
        if (score_0 !== question_0.score) {
          handleScoreChange(score_0, question_0.questionId.toString());
          sendGtmEvent({
            eventName: GTM_EVENT_NAME_ASSESSMENT_SCORE_CHANGE,
            label: question_0.question,
            new_value: score_0,
            old_value: question_0.score
          });
        }
      }} /></li>);
    $[0] = mutateAssessmentQuestions;
    $[1] = selfAssessmentFormData;
    $[2] = setSelfAssessmentFormData;
    $[3] = topic;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t6 = <ol className={t1} data-testid={t2}>{t3}</ol>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <form className={t4} data-testid={t5}>{t6}</form>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
function _temp(item) {
  return item.score;
}
function _temp2(item_2) {
  return {
    questionId: item_2.questionId,
    score: item_2.score
  };
}